//@flow

import * as React from "react"

import { FormattedMessage } from "react-intl"

import { TweenMax, Cubic, Back } from "gsap"
import SVGLoader from "../../UI/SVGLoader"

import {
  Nav,
  NavItem,
  BodyPartIcon,
  NavContent,
  NavTitle,
  NavDescription
} from "./UI"

import heart from "./heart.svg"
import lungs from "./lungs.svg"
import brain from "./brain.svg"

import type { BodyPart } from "./types"

type Props = {
  currentBodyPart: BodyPart,
  appearAfter?: number,
  onSelectBodyPart: (currentBodyPart: BodyPart) => any
}

class Navigation extends React.Component<Props> {
  items: HTMLElement[]
  heartIcon: ?HTMLElement
  lungIcon: ?HTMLElement
  brainIcon: ?HTMLElement
  titles: HTMLElement[]
  animations: TweenMax[]
  css: { grayscale: number }

  constructor(props: Props) {
    super(props)
    this.items = []
    this.heartIcon = null
    this.brainIcon = null
    this.lungIcon = null
    this.titles = []
    this.animations = []
    this.css = { grayscale: 0 }
  }

  componentDidMount() {
    const { appearAfter } = this.props
    const icons = [this.brainIcon, this.lungIcon, this.heartIcon]
    const titles = this.titles.filter(el => el !== null)
    const items = this.items.filter(el => el !== null)
    TweenMax.set(items, { opacity: 0, x: "+=40" })
    TweenMax.set(icons, { scale: 0.5, opacity: 0, y: "+=10" })
    TweenMax.set(titles, { opacity: 0, x: "-=20" })
    TweenMax.staggerTo(
      items,
      0.35,
      { opacity: 1, x: 0, ease: Cubic.easeOut, delay: appearAfter },
      0.15
    )

    TweenMax.staggerTo(
      icons,
      0.5,
      { scale: 1, y: 0, ease: Back.easeOut, delay: appearAfter + 0.25 },
      0.25
    )

    TweenMax.staggerTo(
      icons,
      0.5,
      { opacity: 1, ease: Cubic.easeOut, delay: appearAfter + 0.25 },
      0.25
    )

    TweenMax.staggerTo(
      titles,
      0.35,
      { opacity: 1, x: 0, ease: Cubic.easeOut, delay: appearAfter + 0.35 },
      0.15
    )
  }

  componentDidUpdate(prevProps: Props) {
    const { currentBodyPart } = this.props
    if (prevProps.currentBodyPart !== currentBodyPart) {
      this.interruptAnimations(this.animations)

      const items = this.items.filter(el => el !== null)
      const icons = [this.brainIcon, this.lungIcon, this.heartIcon]

      if (currentBodyPart === "home") {
        this.animations = TweenMax.staggerTo(
          items,
          0.35,
          {
            x: 0,
            boxShadow: "0px 0px 0px 0px rgba(0, 0, 0, 0)",
            ease: Cubic.easeInOut
          },
          0.1
        )
        TweenMax.staggerTo(
          icons,
          0.35,
          {
            y: "0",
            ease: Cubic.easeInOut
          },
          0.1
        )
      } else {
        this.animations = TweenMax.staggerTo(
          items,
          0.35,
          {
            x: "586px",
            ease: Cubic.easeInOut,
            boxShadow: "0px 0px 26px 0px rgba(0, 0, 0, 0.5)"
          },
          0.1
        )
        for (let icon of icons) {
          const y =
            icon === this.heartIcon
              ? "24px"
              : icon === this.lungIcon ? "24px" : 0
          TweenMax.to(
            icon,
            0.35,
            {
              y,
              ease: Cubic.easeInOut
            },
            0.1
          )
        }
      }
    }
  }

  interruptAnimations = (animations: ?(TweenMax[])) => {
    if (animations) {
      for (let animation of animations) {
        animation && animation.kill && animation.kill()
      }
    }
  }

  selectLungs = (e: Event) => {
    const { onSelectBodyPart } = this.props
    e.stopPropagation()
    onSelectBodyPart("respiratory")
  }

  selectHeart = (e: Event) => {
    const { onSelectBodyPart } = this.props
    e.stopPropagation()
    onSelectBodyPart("cardio")
  }

  selectBrain = (e: Event) => {
    const { onSelectBodyPart } = this.props
    e.stopPropagation()
    onSelectBodyPart("neuro")
  }

  render() {
    const { currentBodyPart } = this.props
    const colorable = currentBodyPart === "home"
    return (
      <Nav>
        <NavItem
          selected={currentBodyPart === "neuro"}
          colorable={colorable}
          innerRef={el => this.items.push(el)}
          onClick={this.selectBrain}
        >
          <BodyPartIcon
            top="20px"
            left="50px"
            height="159px"
            width="153px"
            innerRef={el => (this.brainIcon = el)}
          >
            <SVGLoader height="100%" width="100%" path={brain} />
          </BodyPartIcon>
          <NavContent>
            <NavTitle innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.neurological.title" />
            </NavTitle>
            <NavDescription innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.neurological.description" />
            </NavDescription>
          </NavContent>
        </NavItem>
        <NavItem
          selected={currentBodyPart === "respiratory"}
          colorable={colorable}
          innerRef={el => this.items.push(el)}
          onClick={this.selectLungs}
        >
          <BodyPartIcon
            top="-10px"
            left="44px"
            height="182px"
            width="165px"
            innerRef={el => (this.lungIcon = el)}
          >
            <SVGLoader height="100%" width="100%" path={lungs} />
          </BodyPartIcon>
          <NavContent>
            <NavTitle innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.respiratory.title" />
            </NavTitle>
            <NavDescription innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.respiratory.description" />
            </NavDescription>
          </NavContent>
        </NavItem>
        <NavItem
          selected={currentBodyPart === "cardio"}
          colorable={colorable}
          innerRef={el => this.items.push(el)}
          onClick={this.selectHeart}
        >
          <BodyPartIcon
            top="-18px"
            left="70px"
            height="189px"
            width="122px"
            innerRef={el => (this.heartIcon = el)}
          >
            <SVGLoader height="100%" width="100%" path={heart} />
          </BodyPartIcon>
          <NavContent>
            <NavTitle innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.cardio.title" />
            </NavTitle>
            <NavDescription innerRef={el => this.titles.push(el)}>
              <FormattedMessage id="kiosk.body.nav.cardio.description" />
            </NavDescription>
          </NavContent>
        </NavItem>
      </Nav>
    )
  }
}

export default Navigation



// WEBPACK FOOTER //
// ./src/Kiosk/BodyView/Navigation.js